import DataLoader from 'app/ui_v2/components/data-loader/DataLoader';
import { lazy, Suspense } from 'react';
import { Navigate, RouteObject } from 'react-router';

// Lazy load guards and layouts
const HasToBeLoggedInGuard = lazy(() => import('app/core/guards/HasToBeLoggedInGuard'));
const DefaultLayout = lazy(() => import('app/core/layout/DefaultLayout'));
const ClientMessagingRedirectPage = lazy(() => import('app/main/client/messaging/ClientMessagingRedirectPage'));
const Error404Page = lazy(() => import('app/main/errors/Error404Page'));
const HasToBeClientGuard = lazy(() =>
  import('../../guards/HasToBeClientGuard').then((m) => ({ default: m.HasToBeClientGuard })),
);

// Lazy load page components
const JobCreatePage = lazy(() => import('../../../main/client/job-create/JobCreatePage'));
const FindAgency = lazy(() => import('../../../main/client/find-agency/FindAgency'));
const ClientDashboard = lazy(() => import('../../../main/client/dashboard/ClientDashboard'));
const AgencyBookingPage = lazy(() => import('../../../main/client/bookings/agency-booking/AgencyBookingPage'));
const ClientInvoiceDetailsPage = lazy(() => import('../../../main/client/bookings/details/ClientInvoiceDetailsPage'));
const AgencyReceiptDetailsPage = lazy(() => import('../../../main/agency/bookings/details/AgencyReceiptDetailsPage'));
const ClientBookingDetailsPage = lazy(() => import('../../../main/client/bookings/details/ClientBookingDetailsPage'));
const ClientProposalPage = lazy(() => import('../../../main/client/bookings/proposal/ClientProposalPage'));
const ClientReportDetailsPageWrapper = lazy(
  () => import('../../../main/client/bookings/report-details/ClientReportDetailsPageWrapper'),
);
const EventReportReceiptDetailsPage = lazy(
  () => import('../../../main/agency/bookings/details/EventReportReceiptDetailsPage'),
);
const ClientBookingsPage = lazy(() => import('../../../main/client/bookings/ClientBookingsPage'));
const ClientCreateThreadPage = lazy(() => import('../../../main/client/messaging/ClientCreateThreadPage'));
const ClientMessagingPage = lazy(() => import('../../../main/client/messaging/ClientMessagingPage'));
const ClientNotificationLandingPage = lazy(
  () => import('../../../main/client/messaging/ClientNotificationLandingPage'),
);
const ClientSettingsPage = lazy(() => import('../../../main/client/settings/ClientSettingsPage'));
const CompanyWrapper = lazy(() => import('../../../main/client/company/CompanyWrapper'));
const AddUser = lazy(() => import('../../../main/client/company/components/add-user/AddUser'));
const ClientPaymentsPage = lazy(() => import('../../../main/client/payments/ClientPaymentsPage'));
const ClientProfilePage = lazy(() => import('../../../main/client/profile/ClientProfilePage'));
const CheckWrapper = lazy(() => import('../../../main/check-wrapper/CheckWrapper'));
const PaymentModal = lazy(() => import('../../../main/client/payments/payment-modal/PaymentModal'));

const PREFIX = '/client';

export const clientRoutes: RouteObject[] = [
  // REDIRECTS
  {
    path: PREFIX,
    element: <Navigate to={'/client/dashboard'} />,
  },
  {
    path: PREFIX + '/bookings',
    element: <Navigate to={'/client/bookings/pending-matches'} />,
  },
  {
    path: '/cmi',
    element: <ClientMessagingRedirectPage />,
  },
  {
    path: '/cmi/:id',
    element: <ClientMessagingRedirectPage />,
  },
  {
    path: PREFIX + '/messaging',
    element: <Navigate to={'/client/messaging/inbox'} />,
  },
  {
    path: PREFIX + '/settings',
    element: <Navigate to={'/client/settings/general'} />,
  },
  {
    path: PREFIX + '/settings',
    element: <Navigate to={'/client/settings/general'} />,
  },
  {
    path: PREFIX + '/payments',
    element: <Navigate to={'/client/payments/payments-list'} />,
  },
  {
    path: PREFIX + '/profile',
    element: <Navigate to={'/client/profile/main'} />,
  },

  {
    path: PREFIX,
    element: (
      <HasToBeLoggedInGuard>
        <HasToBeClientGuard>
          <DefaultLayout />
        </HasToBeClientGuard>
      </HasToBeLoggedInGuard>
    ),
    errorElement: <Error404Page />,
    children: [
      {
        path: 'job-create/:type',
        element: <JobCreatePage />,
      },
      {
        path: 'job-create',
        element: <JobCreatePage />,
      },
      {
        path: 'find-agency/:id',
        element: <FindAgency />,
      },

      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<DataLoader />}>
            <ClientDashboard />
          </Suspense>
        ),
      },

      // BOOKINGS SECTION
      {
        path: 'bookings/book-agency/:leadId',
        element: <AgencyBookingPage />,
      },
      {
        path: 'bookings/:sectionId/invoices/:invoiceId',
        element: <ClientInvoiceDetailsPage />,
      },
      {
        path: 'bookings/:sectionId/invoices/:invoiceId/receipt',
        element: <AgencyReceiptDetailsPage />,
      },
      {
        path: 'bookings/:sectionId/details/:leadId',
        element: <ClientBookingDetailsPage />,
      },
      {
        path: 'bookings/:sectionId/proposal/:leadId',
        element: <ClientProposalPage />,
      },
      {
        path: 'bookings/:sectionId/report/:reportId',
        element: <ClientReportDetailsPageWrapper />,
      },
      {
        path: 'bookings/:sectionId/report/:reportId/receipt',
        element: <EventReportReceiptDetailsPage />,
      },
      {
        path: 'bookings/:sectionId',
        element: <ClientBookingsPage />,
      },
      // END BOOKINGS SECTION

      // MESSAGES SECTION
      {
        path: 'messaging/create-thread/:recipientId',
        element: <ClientCreateThreadPage />,
      },
      {
        path: 'messaging/create-thread/:recipientId/:jobId',
        element: <ClientCreateThreadPage />,
      },
      {
        path: 'messaging/:sectionId',
        element: <ClientMessagingPage />,
      },
      {
        path: 'messaging/:sectionId/:threadId',
        element: <ClientMessagingPage />,
      },
      {
        path: 'notifications/:messageId',
        element: <ClientNotificationLandingPage />,
      },

      // SETTINGS SECTION

      {
        path: 'settings/:sectionId',
        element: <ClientSettingsPage />,
      },

      // COMPANY & USERS SECTION
      {
        path: 'company/main',
        element: <CompanyWrapper />,
      },
      {
        path: 'company/invite/',
        element: <AddUser />,
      },
      {
        path: 'company/editpermission/',
        element: <AddUser />,
      },

      // PAYMENTS SECTION

      {
        path: 'payments/:id',
        element: <ClientPaymentsPage />,
        children: [
          {
            path: 'replace/:paymentId',
            element: <PaymentModal type="replace" />,
          },
          {
            path: 'edit/:paymentId',
            element: <PaymentModal type="edit" />,
          },
        ],
      },

      {
        path: 'profile/:id',
        element: <ClientProfilePage />,
      },

      {
        path: 'check-wrapper',
        element: <CheckWrapper />,
      },
    ],
  },
];
